/**
 * Reports one version boundary: the selection leaving the card it was in. The editor's other
 * boundaries are the host's focus-out commit, which already covers every action that finishes
 * itself outside the editor (the Add menu and the toolbar both take focus away first).
 *
 * Cutting the version is the host's job — it registers a listener and runs its own versioned
 * commit — which is what keeps the shared editor free of the app's action pipeline.
 */

import { Extension, type Editor } from "@tiptap/core"
import { getContainingCardId } from "../../util/card"

// Keyed by editor in a WeakMap so a destroyed editor is never pinned.
const listeners = new WeakMap<Editor, () => void>()

/**
 * Register the host's version-boundary listener. The listener must only schedule its commit (a
 * microtask) — it is called from inside a ProseMirror transaction. Pair with
 * {@link clearVersionBoundaryListener} on teardown.
 */
export function setVersionBoundaryListener(editor: Editor, listener: () => void): void {
  listeners.set(editor, listener)
}

/** Remove the host's version-boundary listener (host teardown / editor swap). */
export function clearVersionBoundaryListener(editor: Editor): void {
  listeners.delete(editor)
}

/** The card the selection was last seen in — the basis for detecting a card change. */
export interface VersionBoundaryStorage {
  cardId: string | null
}

/**
 * Detects the card-change boundary. Silent on a read-only editor: it makes no edits, so it
 * has no version to cut.
 */
export const VersionBoundary = Extension.create<Record<string, never>, VersionBoundaryStorage>({
  name: "unifiedVersionBoundary",

  addStorage() {
    return { cardId: null }
  },

  onCreate() {
    // Seed from the initial selection so the first real move is measured against where the user
    // actually started, not against "no card".
    this.storage.cardId = getContainingCardId(
      this.editor.state.doc,
      this.editor.state.selection.from,
    )
  },

  onSelectionUpdate() {
    if (!this.editor.isEditable) return

    const previous = this.storage.cardId
    const next = getContainingCardId(this.editor.state.doc, this.editor.state.selection.from)
    this.storage.cardId = next
    // Only leaving a card is a boundary. Arriving from outside every card (the lesson title, the
    // initial content placement) closes nothing, so it cuts nothing.
    if (previous !== null && previous !== next) listeners.get(this.editor)?.()
  },
})
